import { LitElement, css, html, nothing } from 'lit';
import { customElement, state, query } from 'lit/decorators.js';
import '@shoelace-style/shoelace/dist/components/card/card.js';
import '@shoelace-style/shoelace/dist/components/input/input.js';
import '@shoelace-style/shoelace/dist/components/textarea/textarea.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import '@shoelace-style/shoelace/dist/components/dialog/dialog.js';
import '@shoelace-style/shoelace/dist/components/badge/badge.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '../components/denomination-count-grid';
import type { DenominationCountGrid } from '../components/denomination-count-grid';
import { ApiFactory } from '../api/easypos-api';
import {
  ICashDrawer,
  ICashSession,
  ICashSessionReconcileReport,
  IDenominationCount,
} from '../api/model/requests';
import { currencyDenominations } from '../datatypes/currency';
import { WithUserProfileConsumer } from '../mixins/with-user-profile-consumer';
import { WithActiveShopConsumer } from '../mixins/with-active-shop-consumer';
import { styles as sharedStyles } from '../styles/shared-styles';
import { designPrimitives } from '../styles/design-primitives';
import { getOrCreateDeviceId } from '../datatypes/device';
import {
  pendingSalesStore,
  type PendingSalesSnapshot,
} from '../state/pending-sales-store';

const Base = WithActiveShopConsumer(WithUserProfileConsumer(LitElement));

// Operator-facing cash drawer page — ONE register per device. The
// register IS the device; this page shows the device's open/close state
// and the running balance.
@customElement('cash-drawer-page')
export class CashDrawerPage extends Base {
  @state() private _loading = true;
  @state() private _error: string | null = null;
  @state() private _drawer: ICashDrawer | null = null;
  @state() private _session: ICashSession | null = null;
  @state() private _deviceId: string | null = null;
  // B1.5 — server's view: pending cash sales whose movements haven't
  // flushed yet, and stalled rows the drainer can't apply.
  @state() private _pendingOutboxCount = 0;
  @state() private _needsAttentionCount = 0;
  // B1.5 — local PWA view: cash sales queued offline because the network
  // dropped before createSale could return.
  @state() private _localPendingCount = 0;
  // Q.2 - the register shown is the last one the server confirmed, not what it
  // holds right now. Tracked separately from `_error` because they are different
  // things to a cashier: an error means "this failed", stale means "this is real
  // but may have moved on". `_unreachable` is the third case and the reason this
  // page needed rework at all - no cached register AND no server, which must
  // read as "not known" rather than as a closed till.
  @state() private _stale = false;
  @state() private _lastSyncedUtc: string | null = null;
  @state() private _unreachable = false;
  private _pendingSalesUnsub?: () => void;
  @state() private _draining = false;
  @state() private _reconcile: ICashSessionReconcileReport | null = null;

  @state() private _openDialogOpen = false;
  @state() private _openingFloat = '';
  @state() private _openingNote = '';

  @state() private _closeDialogOpen = false;
  @state() private _countedClose = '';
  @state() private _closeNote = '';

  // J3 — the same denomination grid the shop-day bar uses, so the two routes to
  // opening and closing a session record the SAME thing. They did not: this page
  // took a typed total and sent no breakdown at all, so an owner looking at a
  // short till found `ClosingCountsJson` empty with nothing saying why. Same
  // operation, two buttons, two different records.
  @query('#open-grid') private _openGrid?: DenominationCountGrid;
  @query('#close-grid') private _closeGrid?: DenominationCountGrid;

  // Counting by denomination needs a known denomination set for the shop's
  // currency. Where there is none, both routes fall back to a typed total -
  // identical rule to the day bar's, deliberately duplicated as one line rather
  // than shared, because the alternative is a helper that hides which component
  // decides what.
  private get _useGrid(): boolean {
    return currencyDenominations().length > 0;
  }

  // B2 — manual pay-in / pay-out / adjustment dialog state.
  @state() private _movementDialogOpen = false;
  @state() private _movementKind: 'pay-in' | 'pay-out' | 'adjustment' =
    'pay-out';
  @state() private _movementAmount = '';
  @state() private _movementReason = '';

  @state() private _busy = false;

  static styles = [
    sharedStyles,
    designPrimitives,
    css`
      :host {
        display: block;
        max-width: 720px;
        margin: 0 auto;
        padding: 16px;
        box-sizing: border-box;
      }
      .card-head {
        display: flex;
        align-items: center;
        gap: 14px;
        margin-bottom: 14px;
      }
      .card-head__text {
        flex: 1 1 auto;
        min-width: 0;
        display: flex;
        align-items: center;
        gap: 10px;
        flex-wrap: wrap;
      }
      .card-head__name {
        font-family: var(--font-display, inherit);
        font-weight: 700;
        font-size: 1.1rem;
        color: var(--s-ink);
      }
      .meta {
        font-size: 0.82rem;
        color: var(--s-ink-soft, #666);
        margin-top: 4px;
        display: flex;
        flex-wrap: wrap;
        gap: 8px 12px;
        align-items: center;
      }
      .balance-row {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 8px;
        margin: 12px 0;
        font-variant-numeric: tabular-nums;
      }
      .balance-row .cell {
        background: var(--s-canvas-soft, #f7f7f7);
        border-radius: 8px;
        padding: 8px 10px;
      }
      .balance-row .label {
        font-size: 0.75rem;
        color: var(--s-ink-soft, #666);
        text-transform: uppercase;
        letter-spacing: 0.04em;
      }
      .balance-row .val {
        font-size: 1.05rem;
        font-weight: 600;
      }
      .actions {
        display: flex;
        gap: 8px;
        flex-wrap: wrap;
        margin-top: 8px;
      }
      .movements {
        margin-top: 14px;
      }
      .movements h3 {
        margin: 0 0 8px;
        font-size: 0.9rem;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: var(--s-ink-soft, #666);
      }
      .movement-row {
        display: grid;
        grid-template-columns: 1fr auto;
        gap: 6px;
        padding: 6px 0;
        border-bottom: 1px solid var(--s-line, #eee);
        font-variant-numeric: tabular-nums;
      }
      .movement-row .kind {
        font-size: 0.8rem;
        color: var(--s-ink-soft, #666);
      }
      .disabled-note {
        padding: 12px;
        background: var(--s-canvas-soft, #f7f7f7);
        border-radius: 8px;
        color: var(--s-ink-soft, #666);
      }
      .outbox-strip {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-wrap: wrap;
        margin-bottom: 10px;
        font-size: 0.85rem;
      }
      .outbox-strip .pill {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 4px 10px;
        border-radius: 999px;
        background: var(--s-canvas-soft, #f7f7f7);
        border: 1px solid var(--s-line, #e4e4e4);
      }
      .outbox-strip .pill.attention {
        background: rgba(255, 80, 80, 0.08);
        border-color: rgba(255, 80, 80, 0.5);
        color: rgb(180, 30, 30);
      }
      .outbox-strip .pill.local {
        background: rgba(255, 184, 0, 0.08);
        border-color: rgba(255, 184, 0, 0.5);
      }
      .reconcile-row {
        margin: 8px 0 0;
        padding: 8px 12px;
        border-radius: 8px;
        font-size: 0.85rem;
        display: flex;
        align-items: center;
        gap: 10px;
      }
      .reconcile-row.ok {
        background: rgba(0, 160, 80, 0.08);
        border: 1px solid rgba(0, 160, 80, 0.45);
        color: rgb(15, 110, 60);
      }
      .reconcile-row.drift {
        background: rgba(220, 30, 30, 0.08);
        border: 1px solid rgba(220, 30, 30, 0.5);
        color: rgb(160, 25, 25);
      }
      /* Amber, matching the offline-queued pill: the same condition is being
         reported, so it should be recognisable as the same condition. */
      .offline-note {
        margin: 0 0 12px;
        padding: 10px 12px;
        border-radius: 8px;
        font-size: 0.85rem;
        background: rgba(255, 184, 0, 0.08);
        border: 1px solid rgba(255, 184, 0, 0.5);
      }
      .offline-note strong {
        display: block;
        margin-bottom: 2px;
      }
    `,
  ];

  // Tracks the shop we've loaded for so updated() can (re)load when the
  // activeShop context first arrives or changes — see updated().
  private _loadedShopName: string | null = null;

  override connectedCallback(): void {
    super.connectedCallback();
    // B1.5 — subscribe to the local sale-outbox count so the operator
    // sees queued-offline cash sales side-by-side with the server-side
    // pendingOutboxCount.
    this._pendingSalesUnsub = pendingSalesStore.subscribe(
      (snap: PendingSalesSnapshot) => {
        this._localPendingCount = snap.count;
      }
    );
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this._pendingSalesUnsub?.();
    this._pendingSalesUnsub = undefined;
  }

  // On a hard navigation / page refresh this component can mount before
  // the activeShop context has propagated; loading from connectedCallback
  // alone would early-return (no shop) and leave the page stuck on
  // "Loading...". Trigger the load once the shop is available, and re-run
  // it if the active shop changes. Keyed on shop name so it never loops.
  override updated(changed: Map<string | number | symbol, unknown>): void {
    super.updated(changed);
    const shop = this.activeShop?.shopName ?? null;
    if (shop && shop !== this._loadedShopName) {
      this._loadedShopName = shop;
      void this._load();
    }
  }

  // True whenever the figures on this page came from cache, or are not known at
  // all. Every control that asks the SERVER to change the register is gated on
  // it: opening, closing, pay-ins, pay-outs and the flush. A button that takes
  // money and cannot reach the server is a surface with no substance behind it,
  // and the cashier finds out only after they have counted the drawer.
  //
  // Offline SELLING is deliberately not gated: the till queues cash sales and
  // replays them, so the money is captured either way. Recording a manual
  // movement has no such queue - the design decision to give it one, or not, is
  // recorded in docs/decisions.md.
  private get _offline(): boolean {
    return this._stale || this._unreachable;
  }

  /** Server-side writes are impossible while busy, stale, or with no register known. */
  private get _cannotWrite(): boolean {
    return this._busy || this._offline;
  }

  /** "10:42" from the cache timestamp - what a cashier needs is the time, not the date. */
  private get _lastSyncedLabel(): string {
    if (!this._lastSyncedUtc) return 'earlier';
    const when = new Date(this._lastSyncedUtc);
    if (Number.isNaN(when.getTime())) return 'earlier';
    return when.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  private get _moduleEnabled(): boolean {
    const shopName = this.activeShop?.shopName;
    if (!shopName) return false;
    const shop = this.userProfile?.shopAccess?.find(
      (s) => s.shopName.toLowerCase() === shopName.toLowerCase()
    );
    return shop?.subscription?.allowsCashManagement === true;
  }

  private async _load(): Promise<void> {
    if (!this.activeShop) return;
    this._loading = true;
    this._error = null;
    try {
      this._deviceId = await getOrCreateDeviceId();
      const api = ApiFactory.authenticated();
      const r = await api.getMyRegister(
        this.activeShop.shopName,
        this._deviceId
      );
      if (!r.success || !r.data) {
        // No server AND no cache. The register state is genuinely unknown, so
        // the page must say so: every figure below is left untouched rather
        // than defaulted, because a default here would be a claim about money.
        this._unreachable = true;
        this._error = r.error ?? 'Could not load cash register.';
        return;
      }
      this._unreachable = false;
      this._stale = r.stale === true;
      this._lastSyncedUtc = r.lastSyncedUtc ?? null;
      this._drawer = r.data.drawer;
      this._session = r.data.session;
      this._pendingOutboxCount = r.data.pendingOutboxCount ?? 0;
      this._needsAttentionCount = r.data.needsAttentionCount ?? 0;
      // Fetch reconcile report alongside register state so the operator
      // sees drift even before pressing Close. Best-effort.
      //
      // Skipped on a stale read, and that is not laziness: a reconcile report
      // is a statement about drift between sales and movements RIGHT NOW. Run
      // against a register read from cache it would compare figures of
      // different ages and report drift that does not exist - the one number on
      // this page an operator would act on by recounting a till.
      if (this._session && !this._stale) {
        const rec = await api.reconcileCashSession(
          this.activeShop.shopName,
          this._session.id
        );
        this._reconcile = rec.success ? (rec.data ?? null) : null;
      } else {
        this._reconcile = null;
      }
    } finally {
      this._loading = false;
    }
  }

  // B1.5 — Retry pending. Drains BOTH the PWA's local offline outbox AND
  // the server-side CashOutbox for this session. Called automatically by
  // close (server-side) and via this button by the operator on demand.
  private async _retryPending(): Promise<void> {
    if (this._draining || !this.activeShop) return;
    this._draining = true;
    this._error = null;
    try {
      await pendingSalesStore.drainNow();
      if (this._session) {
        const api = ApiFactory.authenticated();
        const r = await api.forceDrainCashSession(
          this.activeShop.shopName,
          this._session.id
        );
        if (!r.success) {
          this._error = r.error ?? 'Could not flush server-side pending sales.';
        }
      }
      await this._load();
    } finally {
      this._draining = false;
    }
  }

  private _movementSum(session: ICashSession): number {
    return session.movements.reduce((a: number, m) => a + m.amount, 0);
  }

  private _expectedClose(session: ICashSession): number {
    return session.openingFloat + this._movementSum(session);
  }

  private async _openOpenDialog(): Promise<void> {
    this._openingFloat = '';
    this._openingNote = '';
    this._openDialogOpen = true;
    // The grid only exists once the dialog has rendered, so reset after the
    // update rather than before it - otherwise a second open shows the previous
    // count still filled in.
    await this.updateComplete;
    this._openGrid?.reset();
  }

  private async _submitOpen(): Promise<void> {
    if (this._busy || !this.activeShop || !this._deviceId) return;
    // The COUNT is the total when the grid is in play - the operator never types
    // it, which is exactly what the manual promises. The scalar path survives for
    // currencies with no denomination set.
    let float: number;
    let counts: IDenominationCount[] | null = null;
    if (this._useGrid) {
      counts = this._openGrid?.counts ?? [];
      float = this._openGrid?.total ?? 0;
    } else {
      float = parseFloat(this._openingFloat);
      if (Number.isNaN(float) || float < 0) {
        this._error = 'Opening float must be a non-negative number.';
        return;
      }
    }
    this._busy = true;
    this._error = null;
    try {
      // J2: opening the cash session is part of STARTING the shift (the session
      // is the shift's overlay). Starting the shift also opens the trading day if
      // this is the day's first shift.
      const api = ApiFactory.authenticated();
      const r = await api.startShift(this.activeShop.shopName, {
        deviceId: this._deviceId,
        openingFloat: float,
        openingNote: this._openingNote || null,
        openingCounts: counts,
      });
      if (!r.success || !r.data) {
        this._error = r.error ?? 'Could not start your shift.';
        return;
      }
      this._openDialogOpen = false;
      await this._load();
    } finally {
      this._busy = false;
    }
  }

  private async _openCloseDialog(): Promise<void> {
    this._countedClose = '';
    this._closeNote = '';
    this._closeDialogOpen = true;
    await this.updateComplete;
    this._closeGrid?.reset();
  }

  // B2 — pay-in / pay-out / adjustment.
  private _openMovementDialog(kind: 'pay-in' | 'pay-out' | 'adjustment'): void {
    this._movementKind = kind;
    this._movementAmount = '';
    this._movementReason = '';
    this._movementDialogOpen = true;
  }

  private async _submitMovement(): Promise<void> {
    if (this._busy || !this.activeShop || !this._session) return;
    const amount = parseFloat(this._movementAmount);
    if (Number.isNaN(amount) || amount <= 0) {
      this._error = 'Amount must be greater than zero.';
      return;
    }
    if (!this._movementReason.trim()) {
      this._error = 'Reason is required.';
      return;
    }
    this._busy = true;
    this._error = null;
    try {
      const api = ApiFactory.authenticated();
      const r = await api.recordCashMovement(
        this.activeShop.shopName,
        this._session.id,
        {
          kind: this._movementKind,
          amount,
          reason: this._movementReason.trim(),
        }
      );
      if (!r.success || !r.data) {
        this._error = r.error ?? 'Could not record cash movement.';
        return;
      }
      this._movementDialogOpen = false;
      await this._load();
    } finally {
      this._busy = false;
    }
  }

  private async _submitClose(): Promise<void> {
    if (this._busy || !this.activeShop || !this._session) return;
    let counted: number;
    let counts: IDenominationCount[] | null = null;
    if (this._useGrid) {
      counts = this._closeGrid?.counts ?? [];
      counted = this._closeGrid?.total ?? 0;
    } else {
      counted = parseFloat(this._countedClose);
      if (Number.isNaN(counted) || counted < 0) {
        this._error = 'Counted close must be a non-negative number.';
        return;
      }
    }
    this._busy = true;
    this._error = null;
    try {
      // J2: closing the cash session is part of ENDING the shift. Ending the
      // last open shift also closes the trading day (the board must be clear).
      const api = ApiFactory.authenticated();
      const r = await api.endShift(this.activeShop.shopName, {
        shiftId: this._session.shiftId,
        countedClose: counted,
        closeNote: this._closeNote || null,
        closingCounts: counts,
      });
      if (!r.success || !r.data) {
        this._error = r.error ?? 'Could not end your shift.';
        return;
      }
      this._closeDialogOpen = false;
      await this._load();
    } finally {
      this._busy = false;
    }
  }

  // Queued work the drawer has not absorbed yet. "Server pending" is the
  // server's own outbox; "Offline queued" is this tablet's, read from local
  // storage and therefore the one figure on this page that stays true with no
  // connection - which is why it renders in the unreachable branch as well.
  //
  // Retry stays enabled when stale, deliberately, where the open/close/pay-in
  // controls do not: it is the operator's "try again now" at the moment they
  // think the signal is back, it is idempotent, and it refreshes this page. The
  // others would commit money against a server this device cannot see.
  private _renderPendingStrip() {
    if (this._pendingOutboxCount === 0 && this._localPendingCount === 0) {
      return nothing;
    }
    return html`<div class="outbox-strip">
      ${
        this._pendingOutboxCount > 0
          ? html`<span class="pill"
              >Server pending:
              <strong>${this._pendingOutboxCount}</strong></span
            >`
          : nothing
      }
      ${
        this._localPendingCount > 0
          ? html`<span class="pill local"
              >Offline queued: <strong>${this._localPendingCount}</strong></span
            >`
          : nothing
      }
      <sl-button
        size="small"
        ?loading=${this._draining}
        ?disabled=${this._draining}
        @click=${this._retryPending}
        >Retry pending</sl-button
      >
    </div>`;
  }

  render() {
    if (!this.activeShop) {
      return html`<p>No active shop.</p>`;
    }
    if (!this._moduleEnabled) {
      return html`
        <div class="page-head">
          <h1 class="page-head__title">Cash register</h1>
        </div>
        <div class="disabled-note">
          The cash management module isn't enabled for this shop. Cash sales
          record their payment method but are not attached to a register or
          session.
        </div>
      `;
    }
    if (this._loading) {
      return html`<p>Loading...</p>`;
    }

    // Nothing from the server and nothing cached. This is the case that used to
    // render as "Register is closed", which is a statement this page had no
    // grounds to make - and the most expensive one it could get wrong, since a
    // cashier acting on it opens a second register on a till that already has
    // one. Say what is actually true instead: the state is not known here.
    if (this._unreachable) {
      return html`
        <div class="page-head">
          <h1 class="page-head__title">Cash register</h1>
        </div>
        <div class="offline-note">
          <strong
            >The register couldn't be loaded, and this device has no saved copy
            of it.</strong
          >
          Whether the till is open isn't known here, so nothing is shown rather
          than guessed. Cash sales rung while offline are still kept on this
          tablet and sync on their own.
        </div>
        ${
          this._error
            ? html`<sl-alert variant="danger" open>${this._error}</sl-alert>`
            : nothing
        }
        ${this._renderPendingStrip()}
      `;
    }

    const session = this._session;
    const isOpen = !!session;
    const registerName = this._drawer?.name ?? 'This device';

    return html`
      <div class="page-head">
        <h1 class="page-head__title">Cash register</h1>
        <p class="page-head__sub">
          One register per device. Open before ringing cash; close at end of
          shift to record the counted total + variance against expected.
        </p>
      </div>

      ${
        this._stale
          ? html`<div class="offline-note">
              <strong
                >Showing the register as it was at
                ${this._lastSyncedLabel}.</strong
              >
              This device can't reach the server, so opening, closing and
              pay-ins are unavailable until it can. Cash sales still ring up and
              are held on the tablet.
            </div>`
          : nothing
      }
      ${
        this._error
          ? html`<sl-alert
              variant="danger"
              open
              closable
              @sl-after-hide=${() => (this._error = null)}
            >
              ${this._error}
            </sl-alert>`
          : nothing
      }
      ${
        this._needsAttentionCount > 0
          ? html`<sl-alert variant="warning" open>
              <strong
                >${this._needsAttentionCount} cash
                movement${this._needsAttentionCount === 1 ? '' : 's'}
                need${this._needsAttentionCount === 1 ? 's' : ''}
                attention.</strong
              >
              The drainer couldn't apply them automatically (e.g. session was
              closed in a race). Use the reconcile report to resolve before
              closing.
            </sl-alert>`
          : nothing
      }
      ${this._renderPendingStrip()}

      <sl-card>
        <div class="card-head">
          <span class="icon-tile">
            <sl-icon name=${isOpen ? 'unlock' : 'lock'}></sl-icon>
          </span>
          <div class="card-head__text">
            <span class="card-head__name">${registerName}</span>
            ${
              isOpen
                ? html`<sl-badge variant="success" pill>Open</sl-badge>`
                : html`<sl-badge variant="neutral" pill>Closed</sl-badge>`
            }
          </div>
        </div>

        ${
          session
            ? html`
                <div class="balance-row">
                  <div class="cell">
                    <div class="label">Opening float</div>
                    <div class="val">${session.openingFloat.toFixed(2)}</div>
                  </div>
                  <div class="cell">
                    <div class="label">Movements (signed)</div>
                    <div class="val">
                      ${this._movementSum(session).toFixed(2)}
                    </div>
                  </div>
                  <div class="cell">
                    <div class="label">Expected close</div>
                    <div class="val">
                      ${this._expectedClose(session).toFixed(2)}
                    </div>
                  </div>
                </div>
                ${
                  this._reconcile
                    ? this._reconcile.drift === 0 &&
                      this._reconcile.missingSaleIds.length === 0 &&
                      this._reconcile.orphanMovementIds.length === 0
                      ? html`<div class="reconcile-row ok">
                          Fully reconciled — sales total
                          ${this._reconcile.salesTotalForSession.toFixed(2)}
                          matches movements
                          ${this._reconcile.movementsSaleTotal.toFixed(2)}.
                        </div>`
                      : html`<div class="reconcile-row drift">
                          Drift detected: sales
                          ${this._reconcile.salesTotalForSession.toFixed(2)} vs
                          movements
                          ${this._reconcile.movementsSaleTotal.toFixed(2)}
                          (delta ${this._reconcile.drift.toFixed(2)}).
                          ${
                            this._reconcile.missingSaleIds.length > 0
                              ? html`${this._reconcile.missingSaleIds.length}
                                sale(s) missing.`
                              : nothing
                          }
                          ${
                            this._reconcile.orphanMovementIds.length > 0
                              ? html`${this._reconcile.orphanMovementIds.length}
                                orphan movement(s).`
                              : nothing
                          }
                        </div>`
                    : nothing
                }

                <div class="actions">
                  <sl-button
                    ?disabled=${this._cannotWrite}
                    @click=${() => this._openMovementDialog('pay-in')}
                    >Pay-in</sl-button
                  >
                  <sl-button
                    ?disabled=${this._cannotWrite}
                    @click=${() => this._openMovementDialog('pay-out')}
                    >Pay-out</sl-button
                  >
                  <sl-button
                    variant="warning"
                    ?disabled=${this._cannotWrite}
                    @click=${this._openCloseDialog}
                    >End shift</sl-button
                  >
                </div>

                <div class="movements">
                  <h3>Movements (${session.movements.length})</h3>
                  ${
                    session.movements.length === 0
                      ? html`<div class="meta">
                          No cash movements yet. Cash sales will appear here.
                        </div>`
                      : session.movements
                          .slice()
                          .reverse()
                          .slice(0, 20)
                          .map(
                            (m) => html`
                              <div class="movement-row">
                                <div>
                                  <div>${m.refSaleId ?? m.reason ?? m.id}</div>
                                  <div class="kind">${m.kind}</div>
                                </div>
                                <div>
                                  ${m.amount > 0 ? '+' : ''}${m.amount.toFixed(2)}
                                </div>
                              </div>
                            `
                          )
                  }
                </div>
              `
            : html`
                <p class="meta">
                  Register is closed. Open the register with the cash currently
                  in the till to start ringing cash sales.
                </p>
                <button
                  class="btn-primary-fill"
                  ?disabled=${this._cannotWrite}
                  @click=${this._openOpenDialog}
                >
                  <sl-icon name="unlock"></sl-icon>
                  Start shift
                </button>
              `
        }
      </sl-card>

      <sl-dialog
        class="full-page-on-phone"
        label="Start shift"
        ?open=${this._openDialogOpen}
        @sl-request-close=${() => (this._openDialogOpen = false)}
      >
        ${
          this._useGrid
            ? html`<p class="meta">
                  Count the cash in the till by denomination. The total is the
                  opening float - you do not type it.
                </p>
                <denomination-count-grid
                  id="open-grid"
                  .denominations=${currencyDenominations()}
                ></denomination-count-grid>`
            : html`<sl-input
                label="Opening float"
                type="number"
                inputmode="decimal"
                step="0.01"
                min="0"
                .value=${this._openingFloat}
                @sl-input=${(e: Event) =>
                  (this._openingFloat = (e.target as HTMLInputElement).value)}
                autofocus
                required
              ></sl-input>`
        }
        <sl-textarea
          label="Note (optional)"
          rows="2"
          .value=${this._openingNote}
          @sl-input=${(e: Event) =>
            (this._openingNote = (e.target as HTMLTextAreaElement).value)}
        ></sl-textarea>
        <sl-button slot="footer" @click=${() => (this._openDialogOpen = false)}
          >Cancel</sl-button
        >
        <sl-button
          slot="footer"
          variant="primary"
          ?loading=${this._busy}
          ?disabled=${this._busy}
          @click=${this._submitOpen}
          >Open</sl-button
        >
      </sl-dialog>

      <sl-dialog
        class="full-page-on-phone"
        label=${
          this._movementKind === 'pay-in'
            ? 'Pay-in (cash in)'
            : this._movementKind === 'pay-out'
              ? 'Pay-out (cash out)'
              : 'Adjustment'
        }
        ?open=${this._movementDialogOpen}
        @sl-request-close=${() => (this._movementDialogOpen = false)}
      >
        <p class="meta">
          ${
            this._movementKind === 'pay-in'
              ? 'Cash entering the till outside of a sale (tip into till, opening float top-up).'
              : this._movementKind === 'pay-out'
                ? 'Cash leaving the till outside of a sale (paid delivery driver, owner takes cash out).'
                : 'Correction. Use sparingly; prefer pay-in or pay-out so the reason is unambiguous.'
          }
        </p>
        <sl-input
          label="Amount"
          type="number"
          inputmode="decimal"
          step="0.01"
          min="0"
          .value=${this._movementAmount}
          @sl-input=${(e: Event) =>
            (this._movementAmount = (e.target as HTMLInputElement).value)}
          autofocus
          required
        ></sl-input>
        <sl-textarea
          label="Reason"
          help-text="Brief description (e.g. 'delivery driver tip', 'owner withdraw for petty cash')."
          rows="2"
          .value=${this._movementReason}
          @sl-input=${(e: Event) =>
            (this._movementReason = (e.target as HTMLTextAreaElement).value)}
          required
        ></sl-textarea>
        <sl-button
          slot="footer"
          @click=${() => (this._movementDialogOpen = false)}
          >Cancel</sl-button
        >
        <sl-button
          slot="footer"
          variant="primary"
          ?loading=${this._busy}
          ?disabled=${this._busy}
          @click=${this._submitMovement}
          >Record</sl-button
        >
      </sl-dialog>

      <sl-dialog
        class="full-page-on-phone"
        label="End shift"
        ?open=${this._closeDialogOpen}
        @sl-request-close=${() => (this._closeDialogOpen = false)}
      >
        ${
          session
            ? html`<p class="meta">
                Expected close:
                <strong>${this._expectedClose(session).toFixed(2)}</strong>
              </p>`
            : nothing
        }
        ${
          this._useGrid
            ? html`<p class="meta">
                  Count the drawer by denomination. The total is your counted
                  close, and the variance is computed against expected.
                </p>
                <denomination-count-grid
                  id="close-grid"
                  .denominations=${currencyDenominations()}
                ></denomination-count-grid>`
            : html`<sl-input
                label="Counted close"
                type="number"
                inputmode="decimal"
                step="0.01"
                min="0"
                .value=${this._countedClose}
                @sl-input=${(e: Event) =>
                  (this._countedClose = (e.target as HTMLInputElement).value)}
                autofocus
                required
              ></sl-input>`
        }
        <sl-textarea
          label="Note (optional)"
          rows="2"
          .value=${this._closeNote}
          @sl-input=${(e: Event) =>
            (this._closeNote = (e.target as HTMLTextAreaElement).value)}
        ></sl-textarea>
        <sl-button slot="footer" @click=${() => (this._closeDialogOpen = false)}
          >Cancel</sl-button
        >
        <sl-button
          slot="footer"
          variant="warning"
          ?loading=${this._busy}
          ?disabled=${this._busy}
          @click=${this._submitClose}
          >End shift</sl-button
        >
      </sl-dialog>
    `;
  }
}
